import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { ChartConfig } from '@/components/ui/chart';

import type { TimeseriesDataPoint } from '../../hooks/useOverviewTimeseries';
import { CSAT_TICKS, formatCsatTick, type MetricChartConfig } from '../../utils/chartConfig';
import { formatDateRangeLabel, isSingleDayRange } from '../../utils/formatDateRangeLabel';
import { formatScore } from '../../utils/formatScore';
import { getComparisonDates } from '../../utils/getComparisonDates';
import { KpiTile } from '../KpiTile/KpiTile';
import { CHART_COLOR } from '../TimeseriesChart/renderTimeseriesLines';
import { TimeseriesChart } from '../TimeseriesChart/TimeseriesChart';

export type FeedbackMetricKey = 'avgCsat' | 'ratedConversations' | 'commentsSent';
// The analytics timeseries has no per-day comments count, so Comments sent has no chart.
export type FeedbackChartKey = Exclude<FeedbackMetricKey, 'commentsSent'>;

export interface FeedbackOverviewCardProps {
  startDate: string;
  endDate: string;
  totals: Record<FeedbackMetricKey, number | null>;
  series: Record<FeedbackChartKey, TimeseriesDataPoint[]>;
  onExport: (format: ExportFormat) => void;
  isExporting?: boolean;
  isLoading?: boolean;
}

const countConfig = (label: string): MetricChartConfig => ({
  dataKey: 'value',
  label,
  domain: [0, 'auto'],
  valueFormatter: (value) => value.toLocaleString(),
  fill: 'zero',
});

const CHARTS: Record<FeedbackChartKey, MetricChartConfig> = {
  avgCsat: {
    dataKey: 'value',
    label: 'analytics.fields.user_csat_column',
    domain: [0, 5],
    valueFormatter: formatScore,
    ticks: CSAT_TICKS,
    tickFormatter: formatCsatTick,
    fill: 'gap',
  },
  ratedConversations: countConfig('analytics.human_agents_overview.conversations_users_rated'),
};

const METRICS: Array<{
  key: FeedbackMetricKey;
  labelKey: string;
  format: (value: number) => string;
}> = [
  {
    key: 'avgCsat',
    labelKey: 'analytics.human_agents_overview.avg_user_csat_score',
    format: (value) => `${formatScore(value)}/5`,
  },
  {
    key: 'ratedConversations',
    labelKey: 'analytics.human_agents_overview.conversations_users_rated',
    format: (value) => value.toLocaleString(),
  },
  {
    key: 'commentsSent',
    labelKey: 'analytics.feedback_overview.comments_sent',
    format: (value) => value.toLocaleString(),
  },
];

export const FeedbackOverviewCard = ({
  startDate,
  endDate,
  totals,
  series,
  onExport,
  isExporting = false,
  isLoading = false,
}: FeedbackOverviewCardProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<FeedbackChartKey>('avgCsat');

  const current = CHARTS[selected];
  const chartConfig = {
    value: { label: t(current.label), color: CHART_COLOR },
  } satisfies ChartConfig;
  const { startDate: comparisonStart, endDate: comparisonEnd } = getComparisonDates(
    startDate,
    endDate
  );

  return (
    <Card className="rounded-lg bg-background text-foreground shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <CardTitle>{t('analytics.feedback_overview.title')}</CardTitle>
            <CardDescription>{formatDateRangeLabel(startDate, endDate)}</CardDescription>
          </div>
          <ExportButton variant="icon" onExport={onExport} isExporting={isExporting} />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          {METRICS.map(({ key, labelKey, format }) => {
            const total = totals[key];
            return (
              <KpiTile
                key={key}
                label={t(labelKey)}
                value={total == null ? '–' : format(total)}
                isLoading={isLoading}
                isSelected={selected === key}
                onClick={key === 'commentsSent' ? undefined : () => setSelected(key)}
              />
            );
          })}
        </div>
        {!isSingleDayRange(startDate, endDate) && (
          <TimeseriesChart
            timeseries={{ isLoading, data: series[selected], onRefetch: () => undefined }}
            chartConfig={chartConfig}
            currentConfig={current}
            t={t}
            period={{ start: startDate, end: endDate }}
            comparisonPeriod={{ start: comparisonStart, end: comparisonEnd }}
          />
        )}
      </CardContent>
    </Card>
  );
};
